import { ComponentType, Overlay } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { inject, Injector, Service } from '@angular/core';
import { take } from 'rxjs';
import { DialogRef } from '@app/core/services/dialog-ref';
import { DIALOG_DATA } from '@app/core/services/dialog-token';

export interface DialogConfig {
  data?: any;
  applyNoPadding?: boolean;
}

@Service()
export class DialogService {
  readonly overlay = inject(Overlay);
  private readonly injector = inject(Injector);
  private readonly openDialogs = new Set<DialogRef>();

  open<T>(component: ComponentType<T>, config?: DialogConfig): DialogRef {
    const positionStrategy = this.overlay.position().global().centerHorizontally().centerVertically();

    const overlayRef = this.overlay?.create({
      positionStrategy,
      hasBackdrop: true,
      backdropClass: 'dialog-overlay',
      panelClass: config?.applyNoPadding ? 'dialog-background' : 'dialog-background-padding',
    });

    const dialogRef = new DialogRef(overlayRef);
    this.openDialogs.add(dialogRef);
    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe(() => this.openDialogs.delete(dialogRef));

    const injector = Injector.create({
      parent: this.injector,
      providers: [
        { provide: DialogRef, useValue: dialogRef },
        { provide: DIALOG_DATA, useValue: config?.data },
      ],
    });
    // Attach component portal to the overlay
    const portal = new ComponentPortal(component, null, injector);
    overlayRef.attach(portal);

    return dialogRef;
  }

  closeAll(): void {
    [...this.openDialogs].forEach(dialogRef => dialogRef.close());
    this.openDialogs.clear();
  }
}
